import { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
      await updateProfile(userCredential.user, { displayName });
      }

    // Enregistrer le pseudo pour permettre la connexion via pseudo
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      uid: userCredential.user.uid,
      displayName: displayName || '',
      email,
      createdAt: new Date(),
      // Compte utilisateur par défaut : profil gratuit (limité)
      accountType: 'FREE',
      userType: 'client', // Regular signup without hairstylist profile = client
    });

    // Envoyer l'email de vérification
    await sendEmailVerification(userCredential.user);

    return userCredential;
  }

  async function login(identifier, password) {
    let emailToUse = identifier;

    // Si l'utilisateur a saisi un pseudo (pas d'@), chercher l'email associé
    if (identifier && !identifier.includes('@')) {
      const q = query(
        collection(db, 'users'),
        where('displayName', '==', identifier.trim()),
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error('Aucun compte ne correspond à ce pseudo. Merci de vérifier le pseudo ou d\'utiliser votre email.');
      }

      emailToUse = snapshot.docs[0].data().email;
    }

    const userCredential = await signInWithEmailAndPassword(auth, emailToUse, password);
    
    return userCredential;
  }

  async function resendVerificationEmail(email, password) {
    // Se connecter temporairement pour pouvoir renvoyer l'email
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    try {
      // Renvoyer l'email de vérification
      await sendEmailVerification(userCredential.user);
    } finally {
      // Se déconnecter après avoir envoyé l'email
      await signOut(auth);
    }
  }

  async function resetPassword(email) {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      throw new Error('Merci de renseigner ton adresse email.');
    }
    await sendPasswordResetEmail(auth, trimmedEmail);
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    logout,
    resendVerificationEmail,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}

